import * as React from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { NavLink } from '../elements/NavLink';
import config from '../utils/Dotenv';
import ModuleWindow, { MEDIA_TYPES } from './ModuleWindow';

export interface ISidebarLink {
  link: string;
  icon: any;
  text: string;
  dataCy: string;
}

export interface IState {
  moduleWindowContent: any;
  moduleWindowOpen: boolean;
  hideMenu: boolean;
  currentPath: string;
  links: Array<ISidebarLink>;
}

export class Sidebar extends React.Component<any> {
  public state: IState = {
    moduleWindowOpen: false,
    moduleWindowContent: {
      contentType: MEDIA_TYPES.VIDEO,
      contentAlt: config.ANIMATION_TITLE,
      contentSrc: config.ANIMATION,
      autoplay: true,
    },
    hideMenu: false,
    currentPath: '',
    links: [],
  };

  constructor(props: any) {
    super(props);
    this.state.currentPath = '/' + window.location.href.split('/').pop() || '/';

    const mediaLink = {
      link: '/media',
      icon: 'fas fa-compact-disc fa-2x',
      text: 'Media',
      dataCy: 'sb-media',
    };

    const documentationLink = {
      link: '/documentation',
      icon: 'fas fa-folder fa-2x',
      text: 'Documentation',
      dataCy: 'sb-documentation',
    };

    const faqLink = {
      link: '/faq',
      icon: 'fa fa-question-circle fa-2x',
      text: 'FAQ',
      dataCy: 'sb-faq',
    };

    const siteLink = {
      link: '/site',
      icon: 'fas fa-map-marked-alt fa-2x',
      text: 'Site',
      dataCy: 'sb-site',
    };

    const homeLink = {
      link: '/',
      icon: 'fas fa-home fa-2x',
      text: 'Home',
      dataCy: 'sb-home',
    };

    const aboutLink = {
      link: '/about',
      icon: 'fas fa-file-alt fa-2x',
      text: 'About',
      dataCy: 'sb-about',
    };

    this.state.links.push(homeLink);
    this.state.links.push(siteLink);
    config.ENABLE_MEDIA && this.state.links.push(mediaLink);
    config.ENABLE_DOCUMENTATION && this.state.links.push(documentationLink);
    config.ENABLE_FAQ && this.state.links.push(faqLink);
    config.ENABLE_ABOUT && this.state.links.push(aboutLink);
  }

  /**
   * When a navigational link is clicked, update the state.
   * This is used to apply the active class to the link.
   * @param newPath the uri of the clicked link.
   */
  private setPath = (newPath: string): void => {
    this.setState({
      currentPath: newPath,
    });

    this.props.rerenderCallback();
  };

  /**
   * Toggle the true/false flag that controls if the sidebar is hidden or not.
   * The flag also controls if the navigation test is shown or not.
   */
  private toggleMenu(): void {
    this.setState({
      hideMenu: !this.state.hideMenu,
    });
  }

  private renderNavLinks(): React.ReactElement[] {
    return this.state.links.map((value, index) => (
      <NavLink
        value={value}
        key={index}
        currentPath={this.state.currentPath}
        hideMenu={this.state.hideMenu}
        setPath={this.setPath}
      />
    ));
  }

  // Close media modal
  private closeModuleWindowOnClick() {
    this.setState({
      moduleWindowOpen: false,
    });
  }

  // Enlarge media modal
  private enlargeMediaOnClick(event: MouseEvent): void {
    event.preventDefault();

    const moduleWindowContent = this.state.moduleWindowContent;

    this.setState({
      moduleWindowContent,
      moduleWindowOpen: !!moduleWindowContent.contentSrc.length,
    });
  }

  public render(): React.ReactElement {
    return (
      <aside
        data-cy="sb"
        className={classNames({
          sidebar: true,
          collapsed: this.state.hideMenu,
        })}
      >
        <button
          className={this.state.hideMenu ? 'jss28' : 'jss28 jss29'}
          data-cy="sb-toggle"
          onClick={(): void => this.toggleMenu()}
        >
          <i className={this.state.hideMenu ? 'fa fa-chevron-right' : 'fa fa-chevron-left'} />
        </button>

        {/* <Link to="/">
          <div className="company-logo-group" data-cy="sb-company-logo">
            <img className="company-logo" src={config.SIDENAV_LOGO} alt="Project Logo" />
            <img
              className={classNames('prism-title', {
                collapsed: this.state.hideMenu,
              })}
              src={require('../img/prism-title.png')}
              alt="Prism by eLIPSE"
            />
          </a>
        </div>
        */}

        {this.state.moduleWindowOpen && (
          <ModuleWindow
            contentType={this.state.moduleWindowContent.contentType}
            contentSrc={this.state.moduleWindowContent.contentSrc}
            contentAlt={this.state.moduleWindowContent.contentAlt}
            closeHandler={() => this.closeModuleWindowOnClick()}
            autoplay={this.state.moduleWindowContent.autoplay}
          />
        )}

        <nav>{this.renderNavLinks()}</nav>
        {config.ENABLE_ANIMATIONS && (
          <nav onClick={(e: any) => this.enlargeMediaOnClick(e)}>
            <a href="#">
              <span className="nav-icon" data-title={this.state.moduleWindowContent.contentAlt} data-cy="sb-icon">
                <i className="fas fa-play-circle fa-2x" />
              </span>
              <span className={'nav-text'} data-cy="sb-text">
                Animation
              </span>
            </a>
          </nav>
        )}

        <Link to="/">
          <div className="company-logo-group" data-cy="sb-company-logo">
            <img
              className={this.state.hideMenu ? 'company-logo' : 'company-logo enlarged'}
              src={config.UQ_LOGO}
              alt="UQ Logo"
            />
          </div>
        </Link>

        <div className="prism-logo-group">
          <a data-cy="sb-elipse-logo" href="https://www.elipse.uq.edu.au/" target="_blank" rel="noopener noreferrer">
            <img className="prismLogo" src={require('../img/prism-logo.svg')} alt="Prism by eLIPSE" />
            <img
              className={classNames('prism-title', {
                collapsed: this.state.hideMenu,
              })}
              src={require('../img/prism-title.png')}
              alt="Prism by eLIPSE"
            />
          </a>
        </div>
      </aside>
    );
  }
}
